
import React from 'react';
import { X, Loader2 } from 'lucide-react';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    isLoading?: boolean;
    onSecondaryConfirm?: () => void;
    isSecondaryLoading?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    isLoading = false,
    onSecondaryConfirm,
    isSecondaryLoading = false
}) => {
    if (!isOpen) return null;

    const [isAnimating, setIsAnimating] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsAnimating(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsAnimating(false);
        }
    }, [isOpen]);

    const handleClose = () => {
        setIsAnimating(false);
        setTimeout(onClose, 500);
    };

    return (
        <div className={`fixed inset-0 z-[1000] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'} !m-0`}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={(isLoading || isSecondaryLoading) ? undefined : handleClose}></div>
            
            {/* Modal Content */}
            <div className={`relative bg-white w-full sm:max-w-[400px] rounded-t-[40px] sm:rounded-[32px] p-8 sm:p-10 shadow-2xl flex flex-col items-center text-center transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                {/* Close Button */}
                {(!isLoading && !isSecondaryLoading) && (
                    <button 
                        onClick={onClose}
                        className="absolute top-6 right-6 text-slate-400 md:hover:text-slate-900 transition-colors"
                    >
                        <X size={24} strokeWidth={2.5} />
                    </button>
                )}

                {/* Text Content */}
                <h3 className="text-[18px] font-black text-[#1e293b] leading-tight mb-6 mt-4">
                    {title}
                </h3>
                
                {description && (
                    <div className="text-[#94a3b8] text-sm font-medium leading-relaxed mb-4 w-full">
                        {description}
                    </div>
                )}

                {/* Buttons Container */}
                <div className="flex w-full gap-4">
                    <button
                        onClick={onConfirm}
                        disabled={isLoading || isSecondaryLoading}
                        className="flex-1 bg-[#ff4d4d] text-white py-5 rounded-[24px] font-bold text-lg md:hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-rose-100 disabled:opacity-50 disabled:shadow-none disabled:active:scale-100 flex items-center justify-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                <span>...</span>
                            </>
                        ) : confirmLabel}
                    </button>
                    <button
                        onClick={onSecondaryConfirm || onClose}
                        disabled={isLoading || isSecondaryLoading}
                        className="flex-1 bg-[#f1f5f9] text-[#1e293b] py-5 rounded-[24px] font-bold text-lg md:hover:bg-[#e2e8f0] active:scale-95 transition-all disabled:opacity-70 disabled:active:scale-100 flex items-center justify-center gap-2"
                    >
                        {isSecondaryLoading ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                <span>...</span>
                            </>
                        ) : cancelLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};
